import React, { useEffect, useState } from 'react';
import { serviceCategories } from '../../config/services';
import { useWizard } from '../../Contexts/WizardContext';
import { Button } from '../General/Button';
import ServiceCard from './ServiceSelectionComponents/ServiceCard';
import { getNearestDatesForServices } from '../../api/endpoints';
import Loader from '../General/Loader';


export const ServiceSelection = () => {
  const { updateFormData, nextStep, previousStep, formData, resetCalendarSubSteps } = useWizard();
  const [servicesByCategory, setServicesByCategory] = useState(formData?.servicesByCategory || serviceCategories.map(cat => {
    return {
      ...cat,
      services: cat.services.map(service => {
        if (service?.tiers) {
          return {
            ...service,
            is_selected: false,
            tiers: service.tiers.map(tier => {
              return {
                ...tier,
                is_selected: false
              }
            })
          }
        } else {
          return {
            ...service,
            is_selected: false
          }
        }
      })

    }
  })) 

  const [selectedServices, setSelectedServices] = useState(formData?.services || [])

  const [isDataLoading, setIsDataLoading] = useState(true)

  useEffect(() => {
    async function fetch() {
      const area = formData.jobsiteData.service_area.service_area
      const category_ids = servicesByCategory.map(el => el.id)

      const nearestAvailableDatesIn2years = await getNearestDatesForServices({
        service_area: area,
        service_type_ids: category_ids,
      })
      
      const categoriesWithDates = servicesByCategory.map(cat => {
        const categoryInResp = nearestAvailableDatesIn2years.find(el => el.service == cat.id)
        const nearestDateAvailable = categoryInResp ? categoryInResp.min_date : false
        return {
          ...cat,
          min_date: nearestDateAvailable 
        }
      })
      setServicesByCategory(categoriesWithDates)
      setIsDataLoading(false)
    }
    fetch()
  }, [])

  useEffect(() => {
    setSelectedServices(
      servicesByCategory.filter(cat => cat.services.some(service => service.is_selected))
        .map(cat => ({
          ...cat,
          services: cat.services.filter(service => service.is_selected)
        }))
    )
  }, [servicesByCategory])

  const handleServiceToggle = (service) => {
    const newSelectedServices = servicesByCategory.map(category => {
      if (category.id === service.service_id) {
        return {
          ...category,
          services: category.services.map(s => {
            if (s.id == service.id) {
              const isTiers = s?.tiers
              if (isTiers) {
                const tiers = s.is_selected ?
                s.tiers.map(tier => { return {...tier, is_selected: false} })
                : s.tiers.map((tier, idx) => { 
                  if (idx === 0) {
                    return {...tier, is_selected: true} 
                  } else return {...tier, is_selected: false} 
                })
                return {
                  ...s,
                  is_selected: !s.is_selected,
                  tiers: tiers
                }
              } else {
                return {
                  ...s,
                  is_selected: !s.is_selected
                }
              }
            } else {
              return s
            }
          })
        }
      } else {
        return category
      }
    })

    setServicesByCategory(newSelectedServices);

    updateFormData(oldVal => { 
        return {
            ...oldVal,
            services: newSelectedServices
        }
    })
  };

  const handleTierChange = (service, tierId) => {
    const newSelectedServices = servicesByCategory.map(category => {
      if (category.id == service.service_id) {
        return {
          ...category,
          services: category.services.map(s => {
            if (s.id == service.id) {
              return {
                ...s,
                tiers: s.tiers.map(tier => {
                  if (tier.id == tierId) {
                    return {
                      ...tier,
                      is_selected: true
                    }
                  } else {
                    return {
                      ...tier,
                      is_selected: false
                    }
                  }
                })
              }
            } else {
              return s
            }
          })
        }
      } else {
        return category
      }
    })

    setServicesByCategory(newSelectedServices);    
  };

  const handleContinue = async () => {
    if (selectedServices.length > 0) {
      const serviceInstances = selectedServices.map(el => el.services).flat().map((service, idx) => {
        let price = service.price
        let serviceId = service.id
        if (service?.tiers) {
          const selectedTier = service.tiers.find(tier => tier.is_selected)
          price = selectedTier.price
          serviceId = selectedTier.id
        }
        const qty = 1
        return {
          instanceId: `${service.id}_${idx}`,
          serviceId: serviceId,
          serviceName: qty > 1
            ? `${service.name} (${idx + 1} of ${qty})`
            : service.name,
          instanceIndex: idx,
          totalInstances: qty,
          price,
          schedulingBucketId: service.service_id,
          status: 'pending',
        }
      })
      resetCalendarSubSteps();
      updateFormData(oldVal => { 
        return {
            ...oldVal,
            services: selectedServices,
            servicesByCategory: servicesByCategory,
            serviceInstances: serviceInstances,
            appointments: []
        }
        })
      nextStep();
    }
  };

  // ---------- Templates --------------
  const summaryBlockTemplate = () => {
    let totalPrice = 0
    return (
      <div className="space-y-2">
        {servicesByCategory.map(category => {
          const services = category.services.filter(service => service.is_selected)
          const servicesWithoutTiers = services.filter(service => !service.tiers)
          totalPrice += servicesWithoutTiers.map(service => service.price).reduce((a,b) => a+b, 0)
          const servicesWithTiers = services.filter(service => service.tiers)
          if (servicesWithTiers.length) {
            const selectedTiers = servicesWithTiers.map(service => service.tiers).flat().filter(tier=> tier.is_selected)
            totalPrice += selectedTiers.map(tier => tier.price).reduce((a,b) => a+b, 0)
          }

          return (
            <div key={category.id}>
              <p className='text-green-800 text-left mb-2'>{services.length ? category.name : ''}</p>
              {services.map(service => {
                let priceLabel
                let nameLabel = service.name;
      
                if (service.tiers) {
                  const tier = service.tiers.find(t => t.is_selected);
                  priceLabel = tier ? `$${tier.price}` : '';
                  nameLabel = tier ? `${service.name} — ${tier.label}` : service.name;
                } else {
                  const price = service.price
                  priceLabel = `$${price}`;
                }
                return (
                  <div key={`summary-${service.id}`} className="flex justify-between items-center text-sm">
                    <span className="text-green-700 pl-10">{nameLabel}</span>
                    <span className="text-green-800 font-medium">
                      {priceLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          )
        })}
        <div className="flex justify-between items-center">
          <span className="text-green-700">Total</span>
          <span className="text-green-800 font-medium">${totalPrice}</span>
        </div>

      </div>
    )
  }

  const actionButtonsTemplate = () => {
    const amount = selectedServices.map(el => el.services).flat().length
    return (
      <div className="mt-8 flex gap-4">
        <Button
          label='Back'
          isDisabled={false}
          onClick={previousStep}
        />
        <Button
            label="Continue"
            isDisabled={amount === 0}
            onClick={handleContinue}
            primary
          />
    </div>
    )
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
          Select Your Services
        </h2>
        <p className="text-gray-600">Choose one or more landscape services you need</p>
        <p className="text-sm text-gray-500 mt-2">Pricing for yard size 0-5000 sq ft</p>
      </div>
    <Loader isLoading={isDataLoading}>
      {!isDataLoading && (<>
        {/* Service Categories */}
        <div className="space-y-8">
          {servicesByCategory.map((category) => (
            <div key={category.id} className="space-y-4">
              <div className="border-b border-gray-200 pb-2">
                <h3 className="text-xl font-bold text-gray-800">{category.name}</h3>
                {category.description && (
                  <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.services.map((service) => {
                  return (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      handleServiceToggle={handleServiceToggle}
                      handleTierChange={handleTierChange}
                      categoryDateAvailable={category.min_date}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Selected Services Summary */}
        {selectedServices.length > 0 && (
          <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="font-semibold text-green-800 mb-3">Selected Services</h3>
            {/* <div className="space-y-2">
              {selectedServices.map((service) => {
                let priceLabel
                let nameLabel = service.name;

                if (service.tiers && service.selectedTierId) {
                  const tier = service.tiers.find(t => t.id === service.selectedTierId);
                  priceLabel = tier ? `$${tier.price}` : '';
                  nameLabel = tier ? `${service.name} — ${tier.label}` : service.name;
                } else {
                  const price = service.pricing ? service.pricing.fullYard : service.price;
                  priceLabel = `$${price}`;
                }

                return (
                  <div key={service.id} className="flex justify-between items-center text-sm">
                    <span className="text-green-700">{nameLabel}</span>
                    <span className="text-green-800 font-medium">
                      {priceLabel}
                    </span>
                  </div>
                );
              })}
            </div> */}
            {summaryBlockTemplate()}
          </div>
        )}
      </>)}


      {/* Navigation Buttons */}
      {actionButtonsTemplate()}
      </Loader>
  </div>
  );
};